import React, { useEffect, useMemo, useRef, useState } from "react";
import { api, formatApiErrorDetail, API, getToken } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { toast } from "sonner";
import {
  Plus, Send, Trash2, Download, Image as ImageIcon, Clock, CheckCircle2, XCircle,
  Share2, Loader2, AlertTriangle, FileDown, Link2, Facebook, Instagram, Music2, Youtube,
  Sparkles, Check, RotateCcw, Pencil,
} from "lucide-react";
import { formatDistanceToNow, parseISO } from "date-fns";
import { de } from "date-fns/locale";
import ChatHint from "../components/ChatHint";

const PLATFORM_META = {
  meta:      { label: "Meta / Facebook", icon: Facebook, color: "text-blue-400" },
  instagram: { label: "Instagram",       icon: Instagram, color: "text-pink-400" },
  tiktok:    { label: "TikTok",          icon: Music2,    color: "text-slate-200" },
  youtube:   { label: "YouTube",         icon: Youtube,   color: "text-red-400" },
};
const ALL_PLATFORMS = ["meta", "instagram", "tiktok", "youtube"];

const STATUS_COLOR = {
  draft: "bg-slate-500/15 text-slate-300 border-slate-500/30",
  scheduled: "bg-indigo-500/15 text-indigo-300 border-indigo-500/30",
  publishing: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  simulated: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  published: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  manual_required: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  partial: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  failed: "bg-red-500/15 text-red-300 border-red-500/30",
};
const STATUS_LABEL = {
  draft: "Entwurf",
  scheduled: "Geplant",
  publishing: "Wird verarbeitet",
  simulated: "Simuliert (alt)",
  published: "Veröffentlicht",
  manual_required: "Manuell nötig",
  partial: "Teilweise",
  failed: "Fehlgeschlagen",
};

const emptyForm = {
  text: "",
  image_id: null,
  image_url: null,
  platforms: [],
  scheduled_for: "",
};

export default function SocialPosts() {
  const [posts, setPosts] = useState([]);
  const [platforms, setPlatforms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [composerOpen, setComposerOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [publishingId, setPublishingId] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [detail, setDetail] = useState(null);
  const [sparkWindow, setSparkWindow] = useState(7);
  const [compareA, setCompareA] = useState(null);
  const [compareB, setCompareB] = useState(null);
  const fileRef = useRef(null);

  const absUrl = (u) => (u?.startsWith("http") ? u : `${API.replace(/\/api$/, "")}${u}`);

  const load = async () => {
    setLoading(true);
    try {
      const [p, plats] = await Promise.all([
        api.get("/social/posts"),
        api.get("/social/platforms"),
      ]);
      setPosts(p.data);
      setPlatforms(plats.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openComposer = () => { setForm(emptyForm); setComposerOpen(true); };

  const togglePlatform = (p) =>
    setForm((f) => ({
      ...f,
      platforms: f.platforms.includes(p) ? f.platforms.filter((x) => x !== p) : [...f.platforms, p],
    }));

  const uploadImage = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      const token = getToken();
      const res = await fetch(`${API}/files/upload`, {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: fd,
      });
      if (!res.ok) throw new Error((await res.json()).detail || "Upload fehlgeschlagen");
      const data = await res.json();
      setForm((f) => ({ ...f, image_id: data.id, image_url: data.url }));
      toast.success("Bild angehängt");
    } catch (e) { toast.error(e.message || "Upload fehlgeschlagen"); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.text.trim()) return toast.error("Text darf nicht leer sein");
    if (form.platforms.length === 0) return toast.error("Mindestens eine Plattform wählen");
    setSubmitting(true);
    try {
      const payload = {
        text: form.text.trim(),
        image_id: form.image_id || null,
        platforms: form.platforms,
        scheduled_for: form.scheduled_for ? new Date(form.scheduled_for).toISOString() : null,
      };
      await api.post("/social/posts", payload);
      toast.success(payload.scheduled_for ? "Beitrag geplant" : "Beitrag als Entwurf gespeichert");
      setComposerOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Konnte Beitrag nicht erstellen");
    } finally { setSubmitting(false); }
  };

  const publishNow = async (post) => {
    setPublishingId(post.id);
    try {
      const { data } = await api.post(`/social/posts/${post.id}/publish-now`);
      const s = data.status;
      if (s === "published") toast.success("Beitrag veröffentlicht");
      else if (s === "simulated") toast.success("Beitrag an verbundene Konten gesendet (simuliert)");
      else if (s === "manual_required") toast("Manuell nötig – Export verfügbar");
      else if (s === "partial") toast("Teilweise verarbeitet – Details prüfen");
      else toast.success("Beitrag verarbeitet");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Fehler beim Veröffentlichen");
    } finally { setPublishingId(null); }
  };

  const remove = async () => {
    try {
      await api.delete(`/social/posts/${confirmDel.id}`);
      toast.success("Beitrag gelöscht");
      setConfirmDel(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const downloadExport = async (post) => {
    try {
      const token = getToken();
      const res = await fetch(`${API}/social/posts/${post.id}/export`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error("Export fehlgeschlagen");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `social-post-${post.id}.txt`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Export heruntergeladen");
    } catch (e) { toast.error(e.message || "Export fehlgeschlagen"); }
  };

  const stats = useMemo(() => ({
    scheduled: posts.filter((p) => p.status === "scheduled").length,
    manual: posts.filter((p) => p.status === "manual_required" || p.status === "partial").length,
    done: posts.filter((p) => ["simulated", "published", "partial"].includes(p.status)).length,
  }), [posts]);

  return (
    <div className="p-6 lg:p-10 max-w-[1400px] mx-auto" data-testid="social-page">
      <header className="flex items-start justify-between mb-6 gap-4">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-emerald-400">Beiträge</div>
          <h1 className="text-3xl font-extrabold tracking-tight mt-1 text-white">Social-Beiträge</h1>
          <p className="text-sm text-slate-400 mt-2">
            {posts.length === 0 ? "Noch leer." : `${posts.length} Beiträge.`}
          </p>
          <div className="mt-2"><ChatHint text={'Sag dem KI-Agenten: „Schlag mir einen Reel-Beitrag vor."'} /></div>
        </div>
        <Button onClick={openComposer} data-testid="add-post-button" className="bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold shadow-lg shadow-emerald-500/20">
          <Plus className="h-4 w-4 mr-2" /> Neuer Beitrag
        </Button>
      </header>

      {/* Platform connection quick-view */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="platform-row">
        {platforms.map((p) => {
          const meta = PLATFORM_META[p.platform];
          const Icon = meta.icon;
          return (
            <Card key={p.platform} className="bg-[#111827] border-white/5" data-testid={`platform-card-${p.platform}`}>
              <CardContent className="p-4 flex items-center gap-3">
                <div className={`h-9 w-9 rounded-lg bg-white/5 grid place-items-center ${meta.color}`}>
                  <Icon className="h-5 w-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-white font-medium truncate">{meta.label}</div>
                  <div className="text-[11px] text-slate-500">
                    {p.connected
                      ? <span className="text-emerald-400">verbunden</span>
                      : p.configured
                        ? <span className="text-amber-400">bereit zur Verbindung</span>
                        : <span className="text-slate-500">nicht konfiguriert</span>}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Card className="bg-[#111827] border-white/5">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-10 text-center text-slate-400">Lade…</div>
          ) : posts.length === 0 ? (
            <div className="p-16 text-center" data-testid="social-empty">
              <Share2 className="h-10 w-10 text-slate-600 mx-auto mb-3" />
              <div className="text-white font-semibold mb-1">Noch keine Social-Beiträge</div>
              <div className="text-sm text-slate-400 mb-5">
                Komponiere einen Beitrag oder lass den KI-Agenten einen vorbereiten.
              </div>
              <Button onClick={openComposer} className="bg-emerald-500 hover:bg-emerald-600"><Plus className="h-4 w-4 mr-2" /> Neuer Beitrag</Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-white/5 hover:bg-transparent">
                  <TableHead className="text-slate-400">Beitrag</TableHead>
                  <TableHead className="text-slate-400">Plattformen</TableHead>
                  <TableHead className="text-slate-400">Status</TableHead>
                  <TableHead className="text-slate-400">Zeit</TableHead>
                  <TableHead className="text-slate-400 text-right">Aktionen</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {posts.map((p) => (
                  <TableRow key={p.id} className="border-white/5 hover:bg-white/[0.02]" data-testid={`post-row-${p.id}`}>
                    <TableCell className="max-w-sm">
                      <div className="flex items-start gap-3">
                        {p.image_id && (
                          <img src={absUrl(`/api/files/${p.image_id}`)} alt="" className="h-10 w-10 rounded object-cover border border-white/10" />
                        )}
                        <div className="min-w-0">
                          <div className="text-sm text-white truncate" title={p.text}>{p.text.slice(0, 80)}{p.text.length > 80 ? "…" : ""}</div>
                          {p.source === "agent" && (
                            <Badge className="mt-1 bg-indigo-500/15 text-indigo-300 border-indigo-500/30 text-[10px]">KI-Agent</Badge>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1 flex-wrap">
                        {p.platforms.map((plat) => {
                          const Icon = PLATFORM_META[plat]?.icon || Share2;
                          return <Icon key={plat} className={`h-4 w-4 ${PLATFORM_META[plat]?.color || "text-slate-300"}`} />;
                        })}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={`${STATUS_COLOR[p.status]} border font-normal`} data-testid={`post-status-${p.id}`}>
                        {STATUS_LABEL[p.status] || p.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-slate-400 text-xs">
                      {p.scheduled_for ? (
                        <span>⏰ {formatDistanceToNow(parseISO(p.scheduled_for), { addSuffix: true, locale: de })}</span>
                      ) : p.published_at ? (
                        <span>✓ {formatDistanceToNow(parseISO(p.published_at), { addSuffix: true, locale: de })}</span>
                      ) : (
                        formatDistanceToNow(parseISO(p.created_at), { addSuffix: true, locale: de })
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm" onClick={() => setDetail(p)}
                        data-testid={`post-detail-${p.id}`} className="text-slate-300 hover:text-white hover:bg-white/5">
                        <Link2 className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm"
                        onClick={() => setCompareA(p)}
                        data-testid={`post-compare-${p.id}`}
                        title="Vergleichen"
                        className="text-slate-300 hover:text-emerald-300 hover:bg-emerald-500/10">
                        ⇄
                      </Button>
                      {["draft", "scheduled"].includes(p.status) && (
                        <Button variant="ghost" size="sm" onClick={() => publishNow(p)} disabled={publishingId === p.id}
                          data-testid={`post-publish-${p.id}`} className="text-emerald-300 hover:text-emerald-200 hover:bg-emerald-500/10">
                          {publishingId === p.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                        </Button>
                      )}
                      <Button variant="ghost" size="sm" onClick={() => downloadExport(p)}
                        data-testid={`post-export-${p.id}`} className="text-slate-300 hover:text-white hover:bg-white/5">
                        <FileDown className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => setConfirmDel(p)}
                        data-testid={`post-delete-${p.id}`} className="text-red-400 hover:text-red-300 hover:bg-red-500/10">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Composer dialog */}
      <Dialog open={composerOpen} onOpenChange={setComposerOpen}>
        <DialogContent className="bg-[#111827] border-white/10 text-slate-100 max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="composer-dialog">
          <DialogHeader>
            <DialogTitle>Neuer Social-Beitrag</DialogTitle>
            <DialogDescription className="text-slate-500">
              Wähle Plattformen, verfasse den Text und plane optional einen Zeitpunkt.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            {/* Essentials — Text + Plattformen */}
            <div>
              <Label className="text-slate-300 text-xs mb-1 block">Was möchtest du teilen?</Label>
              <Textarea rows={5} value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })}
                data-testid="composer-text" placeholder="Schreib hier deinen Beitrag …"
                className="bg-white/5 border-white/10 text-slate-100" required />
              <div className="text-[11px] text-slate-500 mt-1">{form.text.length} Zeichen</div>
            </div>

            <div>
              <Label className="text-slate-300 text-xs mb-2 block">Wo veröffentlichen?</Label>
              <div className="flex flex-wrap gap-2" data-testid="composer-platforms">
                {ALL_PLATFORMS.map((p) => {
                  const meta = PLATFORM_META[p];
                  const Icon = meta.icon;
                  const on = form.platforms.includes(p);
                  return (
                    <button key={p} type="button" onClick={() => togglePlatform(p)}
                      data-testid={`composer-platform-${p}`}
                      className={`text-xs px-3 py-2 rounded-lg border flex items-center gap-2 transition ${
                        on ? "bg-emerald-500/20 border-emerald-500/40 text-emerald-300"
                           : "bg-transparent border-white/10 text-slate-400 hover:text-white"
                      }`}>
                      <Icon className="h-4 w-4" /> {meta.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Erweitert — Bild + Zeitplan */}
            <details className="group border-t border-white/5 pt-3">
              <summary className="cursor-pointer text-xs font-medium text-slate-400 hover:text-slate-200 select-none inline-flex items-center gap-1" data-testid="composer-advanced-toggle">
                <span className="group-open:rotate-90 transition inline-block">▸</span> Erweitert (Bild, Zeitpunkt)
              </summary>
              <div className="space-y-4 mt-3">
                <div>
                  <Label className="text-slate-300 text-xs mb-1 block">Bild anhängen (optional)</Label>
                  {form.image_url ? (
                    <div className="flex items-center gap-3 rounded-lg border border-white/10 p-2 bg-white/[0.03]">
                      <img src={absUrl(form.image_url)} alt="" className="h-14 w-14 object-cover rounded" />
                      <div className="flex-1 text-xs text-slate-400">Bild angehängt</div>
                      <Button type="button" variant="ghost" size="sm" onClick={() => setForm({ ...form, image_id: null, image_url: null })}
                        data-testid="composer-image-remove" className="text-slate-400 hover:text-white">Entfernen</Button>
                    </div>
                  ) : (
                    <>
                      <input ref={fileRef} type="file" accept="image/*" className="hidden"
                        data-testid="composer-image-input"
                        onChange={(e) => e.target.files?.[0] && uploadImage(e.target.files[0])} />
                      <Button type="button" variant="outline" size="sm" onClick={() => fileRef.current?.click()}
                        data-testid="composer-image-upload"
                        className="border-white/10 bg-transparent text-slate-300 hover:bg-white/5 hover:text-white">
                        <ImageIcon className="h-4 w-4 mr-2" /> Bild hochladen
                      </Button>
                    </>
                  )}
                </div>

                <div>
                  <Label className="text-slate-300 text-xs mb-1 block">Erst später veröffentlichen?</Label>
                  <Input type="datetime-local" value={form.scheduled_for}
                    onChange={(e) => setForm({ ...form, scheduled_for: e.target.value })}
                    data-testid="composer-schedule"
                    className="bg-white/5 border-white/10 text-slate-100" />
                  <div className="text-[11px] text-slate-500 mt-1">
                    Leer lassen = jetzt als Entwurf. Zeitpunkt wählen = der Scheduler übernimmt.
                  </div>
                </div>
              </div>
            </details>

            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setComposerOpen(false)} data-testid="composer-cancel">Abbrechen</Button>
              <Button type="submit" disabled={submitting} className="bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold" data-testid="composer-submit">
                {submitting ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Speichere…</> : <>Beitrag anlegen</>}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Post detail */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="bg-[#111827] border-white/10 text-slate-100 max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="post-detail-dialog">
          <DialogHeader>
            <DialogTitle>Beitragsdetails</DialogTitle>
            <DialogDescription className="text-slate-500">Transparente Verarbeitungs-Historie pro Plattform.</DialogDescription>
          </DialogHeader>
          {detail && (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Badge className={`${STATUS_COLOR[detail.status]} border font-normal`}>{STATUS_LABEL[detail.status]}</Badge>
                {detail.source === "agent" && <Badge className="bg-indigo-500/15 text-indigo-300 border-indigo-500/30 text-[10px]">KI-Agent</Badge>}
              </div>
              {detail.image_id && (
                <img src={absUrl(`/api/files/${detail.image_id}`)} alt="" className="w-full max-h-80 object-contain rounded border border-white/10" />
              )}
              <div>
                <div className="text-xs text-slate-400 uppercase tracking-wider mb-1">Basis-Text (Fallback)</div>
                <div className="whitespace-pre-wrap text-sm text-slate-200 bg-white/5 rounded p-3 border border-white/5">{detail.text}</div>
              </div>

              <CaptionVariantsSection
                post={detail}
                onRefresh={async () => {
                  const { data } = await api.get(`/social/posts/${detail.id}`);
                  setDetail(data);
                  load();
                }}
              />

              <div>
                <div className="text-xs text-slate-400 uppercase tracking-wider mb-2">Ergebnisse pro Plattform</div>
                {detail.platforms.map((plat) => {
                  const r = (detail.results || {})[plat];
                  const meta = PLATFORM_META[plat];
                  const Icon = meta.icon;
                  const hasOverride = !!(detail.platform_captions || {})[plat];
                  return (
                    <div key={plat} className="flex items-start gap-3 p-3 rounded border border-white/5 bg-white/[0.02] mb-2" data-testid={`result-${detail.id}-${plat}`}>
                      <Icon className={`h-4 w-4 mt-0.5 ${meta.color}`} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <div className="text-sm text-white">{meta.label}</div>
                          {hasOverride && (
                            <Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-500/30 text-[10px]" data-testid={`override-badge-${plat}`}>
                              Caption-Override aktiv
                            </Badge>
                          )}
                        </div>
                        {r ? (
                          <>
                            <Badge className={`${STATUS_COLOR[r.status]} border font-normal text-[10px] mt-1`}>{STATUS_LABEL[r.status] || r.status}</Badge>
                            <div className="text-xs text-slate-400 mt-1">{r.message}</div>
                            {r.url && (
                              <a href={r.url} target="_blank" rel="noopener noreferrer"
                                data-testid={`result-link-${detail.id}-${plat}`}
                                className="inline-block text-[11px] text-emerald-400 hover:text-emerald-300 mt-1 break-all">
                                → {r.url}
                              </a>
                            )}
                            {r.external_id && <div className="text-[11px] text-slate-500 mt-1 font-mono" data-testid={`result-ext-${detail.id}-${plat}`}>ID: {r.external_id}</div>}
                            {(() => {
                              const s = (detail.stats_cache || {})[plat];
                              if (!s) return null;
                              if (s.status !== "ok") {
                                return (
                                  <div className="text-[11px] text-slate-500 mt-1" data-testid={`stats-${detail.id}-${plat}`}>
                                    📊 {s.message || "Keine Statistik verfügbar."}
                                  </div>
                                );
                              }
                              const series = (detail.stats_history || {})[plat] || [];
                              return (
                                <div className="mt-1" data-testid={`stats-${detail.id}-${plat}`}>
                                  <div className="text-[11px] text-slate-300 flex flex-wrap gap-x-3">
                                    {s.likes != null && <span>👍 {s.likes}</span>}
                                    {s.comments != null && <span>💬 {s.comments}</span>}
                                    {s.views != null && <span>👁 {s.views}</span>}
                                    {s.reach != null && <span>📣 {s.reach}</span>}
                                  </div>
                                  <StatsSparkline series={series} postId={detail.id} platform={plat} windowDays={sparkWindow} />
                                </div>
                              );
                            })()}
                          </>
                        ) : (
                          <div className="text-xs text-slate-500 mt-1">Noch nicht verarbeitet</div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="flex items-center gap-2" data-testid="sparkline-window-group">
                <span className="text-[10px] text-slate-500 uppercase tracking-wider">Verlauf</span>
                {[7, 30, 90].map((d) => (
                  <button key={d} type="button" onClick={() => setSparkWindow(d)}
                    aria-pressed={sparkWindow === d}
                    data-testid={`sparkline-window-${d}`}
                    className={`text-[10px] px-2 py-0.5 rounded-full border transition ${
                      sparkWindow === d
                        ? "border-emerald-500/60 bg-emerald-500/10 text-emerald-200"
                        : "border-white/10 bg-white/[0.02] text-slate-400 hover:bg-white/5"
                    }`}>
                    {d} Tage
                  </button>
                ))}
                <div className="flex-1" />
                {(() => {
                  const hasHistory = Object.values(detail.stats_history || {}).some((arr) => (arr || []).length > 0);
                  return (
                    <button type="button"
                      onClick={async () => {
                        if (!hasHistory) {
                          toast.error("Noch keine Statistik-Daten vorhanden.");
                          return;
                        }
                        try {
                          const url = `${API}/social/posts/${detail.id}/stats/export.csv`;
                          const res = await fetch(url, { headers: { Authorization: `Bearer ${getToken()}` } });
                          if (!res.ok) {
                            const body = await res.json().catch(() => ({}));
                            toast.error(body.detail || "Export fehlgeschlagen");
                            return;
                          }
                          const blob = await res.blob();
                          const dl = document.createElement("a");
                          dl.href = URL.createObjectURL(blob);
                          dl.download = `stats-${detail.id}.csv`;
                          document.body.appendChild(dl);
                          dl.click();
                          dl.remove();
                          toast.success("CSV heruntergeladen");
                        } catch (err) {
                          toast.error("Export fehlgeschlagen");
                        }
                      }}
                      disabled={!hasHistory}
                      title={hasHistory ? "Als CSV exportieren" : "Noch keine Statistik-Daten vorhanden"}
                      data-testid="stats-export-csv"
                      className={`text-[10px] px-2 py-0.5 rounded border transition ${
                        hasHistory
                          ? "border-emerald-500/30 bg-emerald-500/5 text-emerald-300 hover:bg-emerald-500/10"
                          : "border-white/10 bg-white/[0.02] text-slate-500 cursor-not-allowed"
                      }`}>
                      ↓ CSV
                    </button>
                  );
                })()}
              </div>
              {detail.stats_fetched_at && (
                <div className="text-[10px] text-slate-500 flex items-center justify-between" data-testid="stats-fetched-at">
                  <span>Stand: vor {formatDistanceToNow(parseISO(detail.stats_fetched_at), { locale: de })}</span>
                  <Button size="sm" variant="ghost" onClick={async () => {
                    try {
                      const { data } = await api.post(`/social/posts/${detail.id}/stats`);
                      setDetail({ ...detail, stats_cache: data.stats, stats_fetched_at: data.fetched_at, stats_history: data.history });
                      toast.success("Statistik aktualisiert");
                    } catch (err) { toast.error(err.response?.data?.detail || "Aktualisieren fehlgeschlagen"); }
                  }} data-testid="stats-refresh" className="h-6 text-[10px] text-slate-300 hover:text-white">↻ Aktualisieren</Button>
                </div>
              )}
              {!detail.stats_fetched_at && Object.values(detail.results || {}).some((r) => r?.status === "published") && (
                <Button size="sm" variant="outline" onClick={async () => {
                  try {
                    const { data } = await api.post(`/social/posts/${detail.id}/stats`);
                    setDetail({ ...detail, stats_cache: data.stats, stats_fetched_at: data.fetched_at, stats_history: data.history });
                    toast.success("Statistik geladen");
                  } catch (err) { toast.error(err.response?.data?.detail || "Laden fehlgeschlagen"); }
                }} data-testid="stats-fetch" className="border-white/10 bg-transparent text-emerald-300 hover:bg-emerald-500/10">
                  📊 Statistik laden
                </Button>
              )}
              <DialogFooter>
                <Button variant="outline" onClick={() => downloadExport(detail)} data-testid="detail-export"
                  className="border-white/10 bg-transparent text-slate-300 hover:bg-white/5">
                  <FileDown className="h-4 w-4 mr-2" /> Export (txt)
                </Button>
                <Button variant="ghost" onClick={() => setDetail(null)}>Schließen</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmDel} onOpenChange={(o) => !o && setConfirmDel(null)}>
        <AlertDialogContent className="bg-[#111827] border-white/10 text-slate-100">
          <AlertDialogHeader>
            <AlertDialogTitle>Beitrag löschen?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              Der Beitrag wird endgültig entfernt.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="post-delete-cancel">Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={remove} data-testid="post-delete-confirm"
              className="bg-red-500 hover:bg-red-600">Löschen</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {compareA && (
        <CompareDialog a={compareA} b={compareB} posts={posts}
          onPickB={(p) => setCompareB(p)}
          onClose={() => { setCompareA(null); setCompareB(null); }} />
      )}
    </div>
  );
}

const VARIANT_META = {
  tiktok:    { label: "TikTok",    icon: Music2,    color: "text-slate-200", desc: "Hook-first, locker, 1–2 Hashtags" },
  instagram: { label: "Instagram", icon: Instagram, color: "text-pink-400",  desc: "Emoji-reich, Hashtag-Block" },
  youtube:   { label: "YouTube",   icon: Youtube,   color: "text-red-400",   desc: "Länger, SEO-Keywords, Abo-CTA" },
};

function CaptionVariantsSection({ post, onRefresh }) {
  const [generating, setGenerating] = useState(false);
  const [variants, setVariants] = useState(post.platform_captions || {});
  const [drafts, setDrafts] = useState({});
  const [editing, setEditing] = useState({});

  useEffect(() => {
    setVariants(post.platform_captions || {});
    setDrafts({});
    setEditing({});
  }, [post.id, post.platform_captions]);

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post(`/social/posts/${post.id}/caption-variants`);
      setVariants(data.platform_captions || {});
      setDrafts(data.platform_captions || {});
      toast.success("Caption-Varianten erzeugt");
      onRefresh && onRefresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Varianten konnten nicht erzeugt werden");
    } finally { setGenerating(false); }
  };

  const apply = async (plat) => {
    const caption = drafts[plat] ?? variants[plat] ?? "";
    try {
      const { data } = await api.put(`/social/posts/${post.id}/captions/${plat}`, { caption });
      setVariants(data.platform_captions || {});
      setEditing((e) => ({ ...e, [plat]: false }));
      toast.success(`${VARIANT_META[plat].label}-Caption übernommen`);
      onRefresh && onRefresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const reset = async (plat) => {
    try {
      const { data } = await api.put(`/social/posts/${post.id}/captions/${plat}`, { caption: null });
      setVariants(data.platform_captions || {});
      setDrafts((d) => { const n = { ...d }; delete n[plat]; return n; });
      setEditing((e) => ({ ...e, [plat]: false }));
      toast("Zurückgesetzt – Basis-Text wird verwendet");
      onRefresh && onRefresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const hasAny = Object.keys(variants).length > 0;

  return (
    <div data-testid="caption-variants-section">
      <div className="flex items-center justify-between mb-2">
        <div>
          <div className="text-xs text-slate-400 uppercase tracking-wider">Caption-Varianten (plattform-optimiert)</div>
          <div className="text-[11px] text-slate-500">TikTok / Instagram / YouTube. Owner übernimmt pro Plattform.</div>
        </div>
        <Button size="sm" onClick={generate} disabled={generating}
          data-testid="generate-variants-button"
          className="bg-emerald-500 hover:bg-emerald-600 text-white">
          {generating
            ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Erzeuge…</>
            : <><Sparkles className="h-4 w-4 mr-2" />{hasAny ? "Neu erzeugen" : "Varianten generieren"}</>}
        </Button>
      </div>

      {!hasAny && !generating && (
        <div className="text-xs text-slate-500 border border-dashed border-white/10 rounded p-3">
          Noch keine Varianten – Button oben klicken, um 3 plattform-tunable Captions zu erzeugen.
        </div>
      )}

      {hasAny && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {["tiktok", "instagram", "youtube"].map((plat) => {
            const meta = VARIANT_META[plat];
            const Icon = meta.icon;
            const value = (plat in drafts) ? drafts[plat] : (variants[plat] || "");
            const isEditing = !!editing[plat];
            const applied = !!variants[plat];
            return (
              <Card key={plat} className="bg-[#0b0f17] border-white/5" data-testid={`variant-card-${plat}`}>
                <CardContent className="p-3 space-y-2">
                  <div className="flex items-center gap-2">
                    <Icon className={`h-4 w-4 ${meta.color}`} />
                    <div className="text-sm text-white font-medium">{meta.label}</div>
                    {applied && <Badge className="ml-auto bg-emerald-500/15 text-emerald-300 border-emerald-500/30 text-[10px]">übernommen</Badge>}
                  </div>
                  <div className="text-[10px] text-slate-500">{meta.desc}</div>
                  {isEditing ? (
                    <Textarea rows={6} value={value}
                      onChange={(e) => setDrafts((d) => ({ ...d, [plat]: e.target.value }))}
                      data-testid={`variant-textarea-${plat}`}
                      className="text-xs bg-white/5 border-white/10 text-slate-100" />
                  ) : (
                    <div className="whitespace-pre-wrap text-xs text-slate-200 bg-white/5 rounded p-2 border border-white/5 max-h-48 overflow-y-auto"
                      data-testid={`variant-text-${plat}`}>
                      {value || "—"}
                    </div>
                  )}
                  <div className="flex gap-1">
                    <Button size="sm" onClick={() => apply(plat)}
                      data-testid={`variant-apply-${plat}`}
                      className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white h-7 text-xs">
                      <Check className="h-3 w-3 mr-1" /> Übernehmen
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setEditing((e) => ({ ...e, [plat]: !isEditing }))}
                      data-testid={`variant-edit-${plat}`}
                      className="h-7 text-xs border-white/10 bg-transparent text-slate-300 hover:bg-white/5">
                      <Pencil className="h-3 w-3" />
                    </Button>
                    {applied && (
                      <Button size="sm" variant="ghost" onClick={() => reset(plat)}
                        data-testid={`variant-reset-${plat}`}
                        className="h-7 text-xs text-slate-400 hover:text-white">
                        <RotateCcw className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

function CompareDialog({ a, b, posts, onPickB, onClose }) {
  const platforms = Array.from(new Set([...(a?.platforms || []), ...(b?.platforms || [])]));
  const metrics = [
    { k: "likes", l: "👍 Likes" },
    { k: "comments", l: "💬 Kommentare" },
    { k: "views", l: "👁 Aufrufe" },
    { k: "reach", l: "📣 Reichweite" },
  ];
  return (
    <Dialog open={!!a} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#111827] border-white/10 text-slate-100 max-w-3xl max-h-[90vh] overflow-y-auto"
        data-testid="compare-dialog">
        <DialogHeader>
          <DialogTitle>Beiträge vergleichen</DialogTitle>
          <DialogDescription className="text-slate-500">
            Nur echte Statistik-Werte. Fehlende Daten bleiben leer.
          </DialogDescription>
        </DialogHeader>
        {!b ? (
          <div className="space-y-3">
            <div className="text-xs text-slate-400">Wähle einen zweiten Beitrag:</div>
            <div className="max-h-64 overflow-y-auto rounded border border-white/5">
              {posts.filter((p) => p.id !== a.id).map((p) => (
                <button key={p.id} onClick={() => onPickB(p)}
                  data-testid={`compare-pick-${p.id}`}
                  className="w-full text-left px-3 py-2 border-b border-white/5 hover:bg-white/5 text-xs">
                  <div className="text-white truncate">{(p.text || "").slice(0, 60) || p.id}</div>
                  <div className="text-[10px] text-slate-500">{(p.platforms || []).join(", ")} · {p.status}</div>
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div className="space-y-4" data-testid="compare-table">
            <div className="grid grid-cols-2 gap-4">
              {[a, b].map((p, i) => (
                <div key={p.id} className="rounded border border-white/5 bg-white/[0.02] p-3"
                  data-testid={`compare-col-${i === 0 ? "a" : "b"}`}>
                  <div className="text-[10px] text-slate-500 uppercase tracking-wider mb-1">
                    Beitrag {i === 0 ? "A" : "B"}
                  </div>
                  <div className="text-xs text-white truncate">{(p.text || "").slice(0, 80) || p.id}</div>
                  <div className="text-[10px] text-slate-500">{(p.platforms || []).join(", ")} · {p.status}</div>
                </div>
              ))}
            </div>
            {platforms.map((plat) => {
              const sa = (a.stats_cache || {})[plat];
              const sb = (b.stats_cache || {})[plat];
              const okA = sa && sa.status === "ok";
              const okB = sb && sb.status === "ok";
              return (
                <div key={plat} className="rounded border border-white/5 p-3 bg-white/[0.01]"
                  data-testid={`compare-platform-${plat}`}>
                  <div className="text-xs font-semibold text-emerald-300 mb-2">{plat}</div>
                  {!okA && !okB ? (
                    <div className="text-[10px] text-slate-500 italic"
                      data-testid={`compare-empty-${plat}`}>
                      Verlauf erscheint, sobald echte Statistiken gesammelt werden
                    </div>
                  ) : (
                    <div className="space-y-1">
                      {metrics.map((m) => {
                        const va = okA ? sa[m.k] : null;
                        const vb = okB ? sb[m.k] : null;
                        if (va == null && vb == null) return null;
                        const aWins = va != null && (vb == null || va > vb);
                        const bWins = vb != null && (va == null || vb > va);
                        return (
                          <div key={m.k} className="grid grid-cols-5 text-xs items-center gap-2"
                            data-testid={`compare-metric-${plat}-${m.k}`}>
                            <div className={`text-right ${aWins ? "text-emerald-300 font-semibold" : "text-slate-300"}`}>
                              {va != null ? va : "—"}
                            </div>
                            <div className="col-span-3 text-center text-[10px] text-slate-500">{m.l}</div>
                            <div className={`text-left ${bWins ? "text-emerald-300 font-semibold" : "text-slate-300"}`}>
                              {vb != null ? vb : "—"}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-4 mt-2">
                    <StatsSparkline series={(a.stats_history || {})[plat] || []}
                      postId={`cmpA-${a.id}`} platform={plat} windowDays={30} />
                    <StatsSparkline series={(b.stats_history || {})[plat] || []}
                      postId={`cmpB-${b.id}`} platform={plat} windowDays={30} />
                  </div>
                </div>
              );
            })}
          </div>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} data-testid="compare-close">Schließen</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function StatsSparkline({ series, postId, platform, windowDays = 7 }) {
  const points = Array.isArray(series) ? series : [];
  const cutoff = Date.now() - windowDays * 86400000;
  const inWindow = points.filter((p) => {
    const t = p.fetched_at ? Date.parse(p.fetched_at) : NaN;
    return !Number.isNaN(t) && t >= cutoff;
  });
  const metricPriority = ["views", "likes", "reach", "comments"];
  const metric = metricPriority.find((m) => inWindow.filter((p) => p[m] != null).length >= 2);
  const windowPts = metric
    ? inWindow.filter((p) => p[metric] != null).slice(-30)
    : [];
  if (windowPts.length < 2) {
    return (
      <div className="text-[10px] text-slate-500 mt-1 italic" data-testid={`sparkline-empty-${postId}-${platform}`}>
        Verlauf erscheint, sobald echte Statistiken gesammelt werden
      </div>
    );
  }
  const values = windowPts.map((p) => p[metric]);
  const min = Math.min(...values), max = Math.max(...values);
  const span = max - min || 1;
  const W = 72, H = 20, PAD = 1;
  const stepX = (W - PAD * 2) / (windowPts.length - 1);
  const coords = values.map((v, i) => {
    const x = PAD + i * stepX;
    const y = PAD + (H - PAD * 2) * (1 - (v - min) / span);
    return [x, y];
  });
  const d = coords.map(([x, y], i) => `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`).join(" ");
  const last = values[values.length - 1];
  const prev = values[values.length - 2];
  const delta = last - prev;
  const [lx, ly] = coords[coords.length - 1];
  return (
    <div className="flex items-center gap-2 mt-1" data-testid={`sparkline-${postId}-${platform}`}>
      <svg width={W} height={H} className="overflow-visible" aria-label={`Verlauf ${metric}`}>
        <path d={d} fill="none" stroke="#34d399" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
        <circle cx={lx} cy={ly} r="1.8" fill="#34d399" />
      </svg>
      <span className="text-[10px] text-slate-500 font-mono">{metric}</span>
      {delta !== 0 && (
        <span className={`text-[10px] font-mono ${delta > 0 ? "text-emerald-400" : "text-rose-400"}`}
          data-testid={`sparkline-delta-${postId}-${platform}`}>
          {delta > 0 ? "+" : ""}{delta}
        </span>
      )}
    </div>
  );
}

